export interface HttpRequestConfig {
  url: string | URL;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD';
  headers?: HeadersInit;
  body?: BodyInit | null;
  timeoutMs?: number;
}

export interface HttpResponse<T = unknown> {
  status: number;
  ok: boolean;
  url: string;
  headers: Headers;
  data: T;
}

/**
 * Thrown on network errors or timeouts. Non-2xx responses come as HttpResponse.ok === false.
 */
export class HttpError extends Error {
  public readonly status: number;
  public readonly url: string;
  public readonly body?: string;

  constructor(message: string, status: number, url: string, body?: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

export async function httpRequestText(config: HttpRequestConfig): Promise<HttpResponse<string>> {
  const { method = 'GET', headers, body, timeoutMs } = config;

  const urlObject =
    typeof config.url === 'string' ? new URL(config.url, window.location.origin) : config.url;

  const headersObject = new Headers(headers);

  const controller = new AbortController();
  const { signal } = controller;

  let timeoutId: number | undefined;
  if (timeoutMs && timeoutMs > 0) {
    timeoutId = window.setTimeout(() => {
      controller.abort();
    }, timeoutMs);
  }

  try {
    const response = await fetch(urlObject.toString(), {
      method,
      headers: headersObject,
      body,
      signal,
    });

    const text = await response.text();

    return {
      status: response.status,
      ok: response.ok,
      url: response.url,
      headers: response.headers,
      data: text,
    };
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new HttpError('HTTP request timed out', 0, urlObject.toString());
    }

    throw new HttpError(
      error instanceof Error ? error.message : 'Failed to perform HTTP request',
      0,
      urlObject.toString()
    );
  } finally {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
  }
}

export async function httpRequestJson<T>(config: HttpRequestConfig): Promise<HttpResponse<T>> {
  const response = await httpRequestText(config);

  if (!response.data) {
    return { ...response, data: undefined as unknown as T };
  }

  try {
    const parsed = JSON.parse(response.data) as T;
    return { ...response, data: parsed };
  } catch {
    throw new HttpError(
      'Failed to parse JSON response',
      response.status,
      response.url,
      response.data
    );
  }
}
